/**
 * Shared helpers for HTML document artifacts: template linkage and export style IDs.
 */

export type HtmlDocumentArtifactRef = {
  id: string;
  version: number;
  document?: {
    title?: string | null;
    htmlDocumentTemplateId?: string | null;
  } | null;
};

export type HtmlDocumentTemplateBodyRef = {
  bodyTemplate?: string | null;
};

/** Typography/header-footer library record (no reusable body HTML). Matches library Styles list filter. */
export function isHtmlDocumentStyleRecord(template: HtmlDocumentTemplateBodyRef): boolean {
  return !template.bodyTemplate || template.bodyTemplate.trim() === '';
}

/** Library record with reusable body HTML (template). Matches library Templates list filter. */
export function htmlDocumentTemplateHasBody(template: HtmlDocumentTemplateBodyRef): boolean {
  return !!template.bodyTemplate && template.bodyTemplate.trim() !== '';
}

/** Library records usable as export/preview styles (no body content). */
export function filterHtmlDocumentStyleRecords<T extends HtmlDocumentTemplateBodyRef>(
  templates: T[]
): T[] {
  return templates.filter(isHtmlDocumentStyleRecord);
}

/** Returns the library template ID when the artifact was created from a saved template. */
export function getArtifactHtmlDocumentTemplateId(
  artifact: HtmlDocumentArtifactRef
): string | null {
  const id = artifact.document?.htmlDocumentTemplateId;
  if (id == null || id.trim() === '') {
    return null;
  }
  return id;
}

/**
 * Style ID for iframe preview and editor stylesheet injection.
 * Template-backed artifacts always use the linked template; otherwise the popover selection.
 */
export function resolveHtmlDocumentPreviewStyleId(
  linkedTemplateId: string | null,
  selectedExportStyleId: string | null
): string | undefined {
  if (linkedTemplateId != null) {
    return linkedTemplateId;
  }
  return selectedExportStyleId ?? undefined;
}

/**
 * style_id for PDF export mutations. Null when the artifact's linked template drives styling
 * on the backend (same rule as buildRenderUrl before this refactor).
 */
export function resolveHtmlDocumentPdfExportStyleId(
  linkedTemplateId: string | null,
  selectedExportStyleId: string | null
): string | null {
  if (linkedTemplateId != null) {
    return null;
  }
  return selectedExportStyleId;
}
